import { createContext, ReactNode, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import useSWR from 'swr';

import { requestService } from 'shared/utils/services/requestService';
import { useStoreSelector } from 'store/hooks/storeHooks';

const WantsModal = dynamic(() => import('wants/components/WantsModal'), {
  ssr: false,
});

interface IWantedProductTemplates {
  children: ReactNode;
}

export const WantedProductTemplatesContext = createContext(null);

const fetchData = (url) => requestService({ url, method: 'GET' });

export const WantedProductTemplatesProvider = ({ children }: IWantedProductTemplates) => {
  const [wantedProductTemplates, setWantedProductTemplates] = useState<Set<string>>(new Set());
  const [isWantsModalOpen, setIsWantsModalOpen] = useState(false);
  const [productTemplateId, setProductTemplateId] = useState(null);
  const [productTemplateSlug, setProductTemplateSlug] = useState(null);

  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const { data: wantedData } = useSWR(
    isAuthenticated ? '/web-api/v1/wants/wanted_product_templates' : null,
    fetchData,
  );

  useEffect(() => {
    if (wantedData?.wants && wantedData.wants.length > 0) {
      const ids = wantedData.wants.map((want) => String(want.productTemplateId));
      const set: Set<string> = new Set(ids);
      setWantedProductTemplates(set);
    }
  }, [wantedData]);

  const handleWantsModalClose = () => {
    setIsWantsModalOpen(false);
    setProductTemplateId(null);
  };

  const handleWantsModalOpen = (id, slug) => {
    setProductTemplateId(id);
    setProductTemplateSlug(slug);
    setIsWantsModalOpen(true);
  };

  const values = {
    handleWantsModalOpen,
    wantedProductTemplates,
  };

  return (
    <WantedProductTemplatesContext.Provider value={values}>
      {children}
      {isWantsModalOpen && productTemplateId && (
        <WantsModal
          id={productTemplateId}
          slug={productTemplateSlug}
          onClose={handleWantsModalClose}
        />
      )}
    </WantedProductTemplatesContext.Provider>
  );
};
